import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DateFormatter,
  CELL_RENDERER,
  SFAvatar,
  SFDatagrid,
  SFPageContainer,
  SFSkeleton,
  SFCard,
  SFCardBody,
  SFCardHeader,
  SFTypography,
  getStyled,
  SFGridContainer,
  SFGridContainerItem,
  SFGridItem,
  SFDivider,
  SFActionBar,
  SFButton,
  SFAddIcon,
} from "@sf-core";
import { toastActions } from "@base-provider";
import {
  deleteUser,
  editUser,
  getUser,
  restoreUser,
} from "../../../services/userService";
import UserForm from "./userForm";
import { Claims } from "../../../constants/claims";
import { API_PARAM } from "../../../constants/apiParams";
import { GridNames } from "../../../constants/gridConfig";
import { useDispatch } from "react-redux";
import { ACTION_TYPE } from "../../../constants/actionType";
import DatedAction from "../../../components/datedAction";
import ActiveChip from "../../../components/activeChip";
import getActionDefs from "./actionDefs";
import { PATHS } from "../../../constants/paths";

const { styled } = getStyled();

const SFAvatarStyled = styled(SFAvatar)(() => ({
  width: "164px",
  height: "164px",
  alignSelf: "center",
}));

const SFCardStyled = styled(SFCard)(({ theme }) => ({
  overflow: "unset",
  marginBottom: theme.spacing(2.5),
}));

const UserProfile = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const rowHandler = (rowData = null) => {
    navigate(
      PATHS.ADMIN_USERS_EDIT_TENANT_OF_USER.replace(":id", id).replace(
        ":tenantId",
        rowData.id
      )
    );
  };

  const addTenantHandler = () => {
    navigate(PATHS.ADMIN_USERS_ADD_TENANT_TO_USER.replace(":id", id));
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantIdColumn"
        ),
        field: "tenantId",
        hide: true,
        filter: false,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantNameColumn"
        ),
        field: "tenantName",
        minWidth: 180,
        cellRenderer: CELL_RENDERER.EDIT_CLICKABLE,
        cellRendererParams: {
          onClick: rowHandler,
        },
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.roleNameColumn"
        ),
        field: "tenantUserRoleName",
        minWidth: 180,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.roleDescriptionColumn"
        ),
        field: "tenantUserRoleDescription",
        minWidth: 180,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantCreatedAtColumn"
        ),
        field: "createdAt",
        filter: false,
        valueFormatter: DateFormatter,
        minWidth: 180,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantCreatedByColumn"
        ),
        field: "createdBy",
        minWidth: 180,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantUpdatedAtColumn"
        ),
        field: "updatedAt",
        filter: false,
        valueFormatter: DateFormatter,
        minWidth: 180,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantUpdatedByColumn"
        ),
        field: "updatedBy",
        minWidth: 180,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantActiveColumn"
        ),
        field: "isActive",
        cellRenderer: CELL_RENDERER.BOOLEAN,
        headerClass: "header-text-center",
        minWidth: 100,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantDeletedAtColumn"
        ),
        field: "deletedAt",
        filter: false,
        minWidth: 180,
        valueFormatter: DateFormatter,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.tenantDeletedByColumn"
        ),
        field: "deletedBy",
        minWidth: 180,
      },
      {
        headerName: t(
          "usersScreen.userProfileScreen.tenantsGrid.expiresAtColumn"
        ),
        valueFormatter: DateFormatter,
        field: "expiresAt",
        minWidth: 180,
      },
    ],
    []
  );

  const [userData, setUserData] = useState(null);
  const dispatch = useDispatch();

  const getUserData = async () => {
    const res = await getUser(id);
    setUserData(res.data);
  };

  useEffect(() => {
    getUserData();
  }, []);

  const onAction = async (type) => {
    switch (type) {
    case ACTION_TYPE.EDIT:
      await editUser(userData);
      dispatch(
        toastActions.showSuccessMessage(t("usersScreen.messages.editSuccess"))
      );
      navigate(PATHS.ADMIN_USERS);
      break;
    case ACTION_TYPE.DELETE:
      await deleteUser(userData.id);
      dispatch(
        toastActions.showSuccessMessage(
          t("usersScreen.messages.deleteSuccess")
        )
      );
      break;
    case ACTION_TYPE.RESTORE:
      await restoreUser(userData);
      dispatch(
        toastActions.showSuccessMessage(
          t("usersScreen.messages.restoreSuccess")
        )
      );
      break;
    default:
      break;
    }

    getUserData();
  };

  if (!userData) {
    return <SFSkeleton />;
  }

  const leaveHandler = () => navigate(PATHS.ADMIN_USERS);

  const actionDefs = getActionDefs({
    t,
    onCancelClick: leaveHandler,
    onSaveClick: onAction.bind(this, ACTION_TYPE.EDIT),
    onDeleteClick: onAction.bind(this, ACTION_TYPE.DELETE),
    onRestoreClick: onAction.bind(this, ACTION_TYPE.RESTORE),
    userDataProps: userData,
  });

  return (
    <>
      <SFPageContainer>
        <SFActionBar
          actionDefs={actionDefs}
          titleProps={{
            title: t("adminRoutes.userProfile"),
          }}
        />
        <SFCardStyled>
          <SFCardHeader
            title={
              <SFTypography variant="cardSectionHeading">
                {t("usersScreen.userProfileScreen.infoHeader")}
              </SFTypography>
            }
          />
          <SFCardBody>
            <SFGridContainer>
              <SFGridItem xs="auto">
                {userData.avatar ? (
                  <SFAvatarStyled src={userData.avatar} />
                ) : (
                  <SFAvatarStyled name={userData.name} />
                )}
              </SFGridItem>
              <SFGridItem xs>
                <SFGridContainer spacing={2}>
                  <SFGridItem xs={12}>
                    <UserForm
                      data={userData}
                      onChange={(data) => setUserData(data)}
                    />
                  </SFGridItem>
                  <SFGridItem xs={12}>
                    <ActiveChip active={userData.isActive} />
                  </SFGridItem>
                  <SFGridItem xs={12}>
                    <SFDivider />
                  </SFGridItem>
                  <SFGridContainerItem>
                    <SFGridItem xs={6}>
                      <DatedAction
                        user={userData.createdBy}
                        date={userData.createdAt}
                        label={t(
                          "usersScreen.userProfileScreen.createdByLabel"
                        )}
                      />
                    </SFGridItem>
                    <SFGridItem xs={6}>
                      <DatedAction
                        user={userData.updatedBy}
                        date={userData.updatedAt}
                        label={t(
                          "usersScreen.userProfileScreen.updatedByLabel"
                        )}
                      />
                    </SFGridItem>
                    <SFGridItem xs={6}>
                      <DatedAction
                        user={userData.deletedBy}
                        date={userData.deletedAt}
                        label={t(
                          "usersScreen.userProfileScreen.deletedByLabel"
                        )}
                      />
                    </SFGridItem>
                  </SFGridContainerItem>
                </SFGridContainer>
              </SFGridItem>
            </SFGridContainer>
          </SFCardBody>
        </SFCardStyled>
        <SFGridContainer justifyContent="flex-end">
          <SFGridItem xs="auto">
            <SFButton
              variant="primary"
              onClick={addTenantHandler}
              icon={<SFAddIcon />}
            >
              {t("usersScreen.userProfileScreen.addNewLabel")}
            </SFButton>
          </SFGridItem>
        </SFGridContainer>
        <SFDatagrid
          header={
            <SFTypography variant="sectionTitle">
              {t("usersScreen.userProfileScreen.gridHeader")}
            </SFTypography>
          }
          permissions={{
            add: Claims.AdminUsersAdd,
            edit: Claims.AdminUsersEdit,
            delete: Claims.AdminUsersDelete,
          }}
          oldFiltersPanel={true}
          columnDefs={columnDefs}
          gridName={GridNames.USER_TENANTS_GRID}
          datasourceUrl={API_PARAM.TENANT_USER_GRID.replace(/{id}/, id)}
          filterValuesUrl={API_PARAM.TENANTS_USER_COL_FILTER.replace(
            /{id}/,
            id
          )}
        />
      </SFPageContainer>
    </>
  );
};

export default UserProfile;
